/**
 * MIO — sistema i18n leve, sem dependências externas.
 *
 * Uso:
 *   import { t, useT, setLang, getLang, LANGS } from "@/lib/i18n";
 *
 *   // hook reativo
 *   const txt = useT();
 *   txt("nav.dashboard")  →  "Painel" / "Dashboard" / ...
 *
 *   // standalone
 *   t("nav.dashboard")
 *
 *   // alterar idioma (na SettingsPage)
 *   setLang("en")
 *
 * Regras inegociáveis:
 *   - PT-PT é o idioma base e fallback.
 *   - Termos clínicos críticos (precauções, hipóteses, disclaimer) PERMANECEM
 *     em PT-PT mesmo após troca de idioma — protegem responsabilidade clínica.
 *     A tradução é apenas de chrome (navegação, settings, ações genéricas).
 *   - Se chave não existir, devolve a string PT-PT.
 *   - Se ainda assim não existir, devolve a própria chave.
 *
 * Sem backend. Sem dependências. Apenas localStorage + dicionário JSON.
 */

const STORAGE_KEY = "mio:lang";

export const LANGS = [
  { code: "pt-PT", label: "Português (Portugal)", flag: "🇵🇹", base: true },
  { code: "en",    label: "English",              flag: "🇬🇧" },
  { code: "es",    label: "Español",              flag: "🇪🇸" },
  { code: "fr",    label: "Français",             flag: "🇫🇷" },
  { code: "de",    label: "Deutsch",              flag: "🇩🇪" },
  { code: "it",    label: "Italiano",             flag: "🇮🇹" },
];

// Dicionário inicial — apenas chrome neutral. Não traduzir clínica.
const DICT = {
  "pt-PT": {
    "nav.dashboard": "Painel",
    "nav.patients": "Pacientes",
    "nav.reports": "Relatórios",
    "nav.matrix": "MIO Matrix",
    "nav.calendar": "Agenda",
    "nav.reminders": "Lembretes",
    "nav.settings": "Definições",
    "settings.title": "Definições",
    "settings.language.title": "Idioma da interface",
    "settings.language.body": "Idioma usado na navegação e nas ações gerais. Os termos clínicos (Análise Clínica, Precauções, MIO Matrix) permanecem em Português por integridade clínica.",
    "settings.language.note": "A preferência fica guardada apenas neste dispositivo.",
    "settings.language.save": "Aplicar idioma",
    "common.save": "Guardar",
    "common.cancel": "Cancelar",
    "common.close": "Fechar",
    "common.back": "Voltar",
  },
  "en": {
    "nav.dashboard": "Dashboard",
    "nav.patients": "Patients",
    "nav.reports": "Reports",
    "nav.matrix": "MIO Matrix",
    "nav.calendar": "Calendar",
    "nav.reminders": "Reminders",
    "nav.settings": "Settings",
    "settings.title": "Settings",
    "settings.language.title": "Interface language",
    "settings.language.body": "Language used for navigation and general actions. Clinical terms (Clinical Analysis, Precautions, MIO Matrix) remain in Portuguese for clinical integrity.",
    "settings.language.note": "Preference is stored on this device only.",
    "settings.language.save": "Apply language",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.close": "Close",
    "common.back": "Back",
  },
  "es": {
    "nav.dashboard": "Panel",
    "nav.patients": "Pacientes",
    "nav.reports": "Informes",
    "nav.matrix": "MIO Matrix",
    "nav.calendar": "Agenda",
    "nav.reminders": "Recordatorios",
    "nav.settings": "Ajustes",
    "settings.title": "Ajustes",
    "settings.language.title": "Idioma de la interfaz",
    "settings.language.body": "Idioma usado para la navegación y acciones generales. Los términos clínicos (Análisis Clínico, Precauciones, MIO Matrix) permanecen en portugués por integridad clínica.",
    "settings.language.note": "La preferencia se guarda solo en este dispositivo.",
    "settings.language.save": "Aplicar idioma",
    "common.save": "Guardar",
    "common.cancel": "Cancelar",
    "common.close": "Cerrar",
    "common.back": "Volver",
  },
  "fr": {
    "nav.dashboard": "Tableau de bord",
    "nav.patients": "Patients",
    "nav.reports": "Rapports",
    "nav.matrix": "MIO Matrix",
    "nav.calendar": "Agenda",
    "nav.reminders": "Rappels",
    "nav.settings": "Paramètres",
    "settings.title": "Paramètres",
    "settings.language.title": "Langue de l'interface",
    "settings.language.body": "Langue utilisée pour la navigation et les actions générales. Les termes cliniques (Analyse Clinique, Précautions, MIO Matrix) restent en portugais par intégrité clinique.",
    "settings.language.note": "La préférence est enregistrée uniquement sur cet appareil.",
    "settings.language.save": "Appliquer la langue",
    "common.save": "Enregistrer",
    "common.cancel": "Annuler",
    "common.close": "Fermer",
    "common.back": "Retour",
  },
  "de": {
    "nav.dashboard": "Übersicht",
    "nav.patients": "Patienten",
    "nav.reports": "Berichte",
    "nav.matrix": "MIO Matrix",
    "nav.calendar": "Kalender",
    "nav.reminders": "Erinnerungen",
    "nav.settings": "Einstellungen",
    "settings.title": "Einstellungen",
    "settings.language.title": "Oberflächensprache",
    "settings.language.body": "Sprache für Navigation und allgemeine Aktionen. Klinische Begriffe (Klinische Analyse, Vorsichtsmaßnahmen, MIO Matrix) bleiben aus klinischer Integrität auf Portugiesisch.",
    "settings.language.note": "Die Einstellung wird nur auf diesem Gerät gespeichert.",
    "settings.language.save": "Sprache anwenden",
    "common.save": "Speichern",
    "common.cancel": "Abbrechen",
    "common.close": "Schließen",
    "common.back": "Zurück",
  },
  "it": {
    "nav.dashboard": "Pannello",
    "nav.patients": "Pazienti",
    "nav.reports": "Rapporti",
    "nav.matrix": "MIO Matrix",
    "nav.calendar": "Agenda",
    "nav.reminders": "Promemoria",
    "nav.settings": "Impostazioni",
    "settings.title": "Impostazioni",
    "settings.language.title": "Lingua dell'interfaccia",
    "settings.language.body": "Lingua utilizzata per la navigazione e le azioni generali. I termini clinici (Analisi Clinica, Precauzioni, MIO Matrix) rimangono in portoghese per integrità clinica.",
    "settings.language.note": "La preferenza viene salvata solo su questo dispositivo.",
    "settings.language.save": "Applica lingua",
    "common.save": "Salva",
    "common.cancel": "Annulla",
    "common.close": "Chiudi",
    "common.back": "Indietro",
  },
};

import { useEffect, useState, useCallback } from "react";

function detectInitialLang() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && DICT[stored]) return stored;
  } catch { /* SSR / privacy mode */ }
  return "pt-PT";
}

let _currentLang = detectInitialLang();
const _listeners = new Set();

export function getLang() { return _currentLang; }

export function setLang(code) {
  if (!DICT[code]) return false;
  _currentLang = code;
  try { localStorage.setItem(STORAGE_KEY, code); } catch { /* */ }
  _listeners.forEach(fn => { try { fn(code); } catch { /* */ } });
  return true;
}

export function t(key, lang) {
  const code = lang || _currentLang;
  return (DICT[code] && DICT[code][key]) || DICT["pt-PT"][key] || key;
}

/** Hook reativo: re-renderiza componentes ao mudar idioma. */
export function useT() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick(x => x + 1);
    _listeners.add(fn);
    return () => { _listeners.delete(fn); };
  }, []);
  return useCallback((key) => t(key), []);
}
